import type {TFunction} from 'i18next';
import type {CommandSearchCategoryLayout} from '../components/panel-command-search';
import {DEFAULT_COMMAND_SEARCH_CATEGORY_LAYOUTS} from '../components/panel-command-search';
import type {DynamicSearchIndustry} from './adapters/dynamic-providers';

export const OPERATOR_COMMAND_SEARCH_CATEGORY_ORDER = [
    'recent',
    'pages',
    'actions',
    'tutorials',
    'vinDecoder',
    'nexus',
    'customers',
    'quotes',
    'salesOrders',
    'backOrders',
    'invoices',
    'credits',
    'warranty',
] as const;

export const NEXUS_INDUSTRY_CATEGORY_PREFIX = 'nexus-';

/** Category id for a per-industry Nexus suggestion group. */
export const buildNexusIndustryCategoryId = (industryId: string | number): string =>
    `${NEXUS_INDUSTRY_CATEGORY_PREFIX}${industryId}`;

/**
 * Builds category order with per-industry Nexus slots when suggestions and/or
 * catalogue part-lookup results are shown.
 *
 * - Suggestions on: replace the single `nexus` slot with per-industry groups.
 * - Suggestions off but industries expanded (part lookup): keep `nexus` for keyword
 *   rows and append per-industry slots for part hits.
 */
export const createCommandSearchCategoryOrder = (
    industries: DynamicSearchIndustry[] | undefined,
    canSeeAutocompleteSuggestions: boolean,
    expandIndustryCategories = canSeeAutocompleteSuggestions,
): string[] => {
    const base = [...OPERATOR_COMMAND_SEARCH_CATEGORY_ORDER];
    if (!expandIndustryCategories || !industries?.length) {
        return base;
    }

    const nexusIndex = base.indexOf('nexus');
    if (nexusIndex < 0) {
        return base;
    }

    const industryCategories = industries.map((industry) => buildNexusIndustryCategoryId(industry.id));

    if (canSeeAutocompleteSuggestions) {
        return [
            ...base.slice(0, nexusIndex),
            ...industryCategories,
            ...base.slice(nexusIndex + 1),
        ];
    }

    return [
        ...base.slice(0, nexusIndex + 1),
        ...industryCategories,
        ...base.slice(nexusIndex + 1),
    ];
};

/** Builds translated category labels for the command palette. `t` uses the `panel-sections.command-search` keyPrefix. */
export const createCommandSearchCategoryLabels = (
    t: TFunction<'components', 'panel-sections.command-search'>,
    industries?: DynamicSearchIndustry[],
): Record<string, string> => {
    const labels: Record<string, string> = {
        recent: t((s) => s.categories.recent),
        pages: t((s) => s.categories.pages),
        actions: t((s) => s.categories.actions),
        tutorials: t((s) => s.categories.tutorials),
        vinDecoder: t((s) => s.categories.vinDecoder),
        nexus: t((s) => s.categories.nexus),
        customers: t((s) => s.categories.customers),
        quotes: t((s) => s.categories.quotes),
        salesOrders: t((s) => s.categories.salesOrders),
        backOrders: t((s) => s.categories.backOrders),
        invoices: t((s) => s.categories.invoices),
        credits: t((s) => s.categories.credits),
        warranty: t((s) => s.categories.warranty),
    };

    industries?.forEach((industry) => {
        labels[buildNexusIndustryCategoryId(industry.id)] = industry.title ?? industry.name ?? String(industry.id);
    });

    return labels;
};

export const OPERATOR_COMMAND_SEARCH_CATEGORY_LAYOUTS: Record<string, CommandSearchCategoryLayout> = {
    ...DEFAULT_COMMAND_SEARCH_CATEGORY_LAYOUTS,
    tutorials: {
        variant: 'grid',
        gridSize: {xs: 12, sm: 6, md: 6},
    },
};
